import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, CornerDownLeft } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import { buildSearchIndex, searchContent } from "../../lib/search";
import { resolveUrl } from "../../lib/media";
import { useScrollLock } from "../../lib/useScrollLock";

export const SearchOverlay = ({ open, onClose }) => {
  const { content } = useContent();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  useScrollLock(open);
  const index = useMemo(() => buildSearchIndex(content), [content]);
  const results = useMemo(() => searchContent(index, q), [index, q]);

  useEffect(() => {
    if (open) { setQ(""); setTimeout(() => inputRef.current?.focus(), 100); }
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const goTo = (href) => { onClose(); navigate(href); if (href.includes("#")) window.location.hash = href.split("#")[1]; };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[10001] bg-black/85 backdrop-blur-md flex items-start justify-center pt-[12vh] px-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="search-overlay">
          <motion.div onClick={(e) => e.stopPropagation()} initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}
            className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0a0a0a] overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <Search size={20} className="text-electric shrink-0" />
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
                placeholder="Rechercher un projet, une formation, une actualité…"
                className="flex-1 bg-transparent text-white text-lg placeholder:text-white/30 outline-none" />
              <button onClick={onClose} data-testid="search-close" className="text-white/40 hover:text-white"><X size={20} /></button>
            </div>
            <div className="max-h-[55vh] overflow-y-auto">
              {q.trim() === "" ? (
                <div className="p-8 text-center text-secondary2 text-sm">Commencez à taper pour rechercher dans tout le site.</div>
              ) : results.length === 0 ? (
                <div className="p-8 text-center text-secondary2" data-testid="search-no-results">Aucun résultat trouvé</div>
              ) : (
                <ul data-testid="search-results">
                  {results.map((r, i) => (
                    <li key={i}>
                      <button onClick={() => goTo(r.href)} data-testid={`search-result-${i}`}
                        className="w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-white/5 transition-colors border-b border-white/5 last:border-0 group">
                        <div className="h-12 w-12 rounded-lg overflow-hidden bg-[#141414] border border-white/10 shrink-0">
                          {r.image && <img src={resolveUrl(r.image)} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] tracking-[0.2em] uppercase text-electric mb-0.5">{r.type}{r.subtitle ? ` · ${r.subtitle}` : ""}</div>
                          <div className="text-white truncate">{r.title}</div>
                        </div>
                        <CornerDownLeft size={15} className="text-white/20 group-hover:text-electric transition-colors shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
